/**
 * Shared vertical-spread configuration: DB loader + rating cfg builder.
 *
 * **Source of truth**
 * - Per-account values: `account_strategy_settings` / `account_strategy_defaults`
 * - Agent code defaults: Rust `vs_defaults()` in plugins/vertical_spreads/defaults.rs
 *
 * Frontend no longer ships a parallel DEFAULTS map (removed / deprecated).
 */

import { apiGet } from "@/lib/api";
import { parsePoints } from "@/components/slider";
import { cfgFromEntries } from "@/lib/strategy-config-keys";
import { OPTION_MULTIPLIER } from "@/lib/option-multiplier";

// ---------------------------------------------------------------------------
// Config loader — account_strategy_settings only
// ---------------------------------------------------------------------------

interface ConfigEntry {
  key: string;
  value: string;
}

/** Load per-account VS config from `account_strategy_settings`. Code defaults
 *  are not mirrored in the browser; the agent uses Rust `vs_defaults()`.
 *  Without an account hash, returns `{}`. */
export async function loadVsConfig(accountHash?: string): Promise<Record<string, string>> {
  if (!accountHash) return {};
  const mainEntries = await apiGet<ConfigEntry[]>(`/strategies/account-settings/vertical_spreads?account=${encodeURIComponent(accountHash)}`).catch(() => []);
  return applyEnableDefaults(cfgFromEntries(mainEntries));
}

// ---------------------------------------------------------------------------
// Sizing — per-direction per-spread max-loss budget
// ---------------------------------------------------------------------------

/** Per-direction per-spread max-loss budget in dollars. */
export interface PositionSizes {
  bull: number; // bull put
  bear: number; // bear call
}

/** Per-spread dollar budget per direction (mirrors the agent's
 *  `rating::dir_size_budget`): each side picks one method — % → pct × account
 *  value, $ → the entered amount, # contracts → 0 (count-based, no budget). */
export function positionSizesFor(cfg: Record<string, string>, accountValue: number): PositionSizes {
  const on = (k: string, def: boolean) => (cfg[k] !== undefined ? cfg[k] !== "false" : def);
  const dir = (p: "bull" | "bear") => {
    if (on(`vs_${p}_size_contracts_enabled`, false)) return 0;
    if (on(`vs_${p}_size_dollars_enabled`, false)) return Number(cfg[`vs_${p}_size_dollars`]) || 0;
    return ((Number(cfg[`vs_${p}_size_pct`]) || 0) / 100) * accountValue;
  };
  return { bull: dir("bull"), bear: dir("bear") };
}

/** Select the budget for a given spread direction. */
export function positionSizeFor(sizes: PositionSizes, spreadType: "bull_put" | "bear_call"): number {
  return spreadType === "bull_put" ? sizes.bull : sizes.bear;
}

/** Contract count for a spread, honoring the sizing method: a fixed count when
 *  the direction sizes by an absolute number of contracts, otherwise
 *  floor(dollar budget / per-contract max loss). `maxLossPerShare` is the
 *  spread's max loss per share (× OPTION_MULTIPLIER = per contract). Mirrors the Rust engine. */
export function contractsForSpread(
  cfg: Record<string, string>,
  sizes: PositionSizes,
  spreadType: "bull_put" | "bear_call",
  maxLossPerShare: number,
): number {
  const side = spreadType === "bull_put" ? "bull" : "bear";
  if (cfg[`vs_${side}_size_contracts_enabled`] === "true") {
    return Math.max(0, Math.trunc(Number(cfg[`vs_${side}_size_contracts`] ?? "1") || 0));
  }
  const budget = positionSizeFor(sizes, spreadType);
  const mlpc = maxLossPerShare * OPTION_MULTIPLIER;
  return mlpc > 0 && budget > 0 ? Math.floor(budget / mlpc) : 0;
}

// ---------------------------------------------------------------------------
// Rating config builder — converts string config map to typed rateOption() input
// ---------------------------------------------------------------------------

export interface RatingCfg {
  baSpreadPctWeight: number; baSpreadPctEnabled: boolean;
  probItmWeight: number; probItmEnabled: boolean;
  hvPctWeight: number; hvPctEnabled: boolean;
  ivHvWeight: number; ivHvEnabled: boolean;
  dteEnabled: boolean;
  dteWeight: number;
  oiEnabled: boolean;
  /** Open-interest go/no-go gate (ZoneSlider). `minOi` is the threshold; the
   * left/right zones decide whether OI below/above it is accepted (>=0) or
   * rejected (<0). Default: reject below, accept above (a min floor). Applied to
   * BOTH legs of a spread. From `vs_min_oi` / `vs_min_oi_left_y` / `_right_y`. */
  minOi: number; minOiLeftY: number; minOiRightY: number;
  dtePoints: [number, number][]; dteLeftY: number; dteRightY: number;
  hvPctPoints: [number, number][]; hvPctLeftY: number; hvPctRightY: number;
  baSpreadPctPoints: [number, number][]; baSpreadPctLeftY: number; baSpreadPctRightY: number;
  ivHvPoints: [number, number][]; ivHvLeftY: number; ivHvRightY: number;
  probItmPoints: [number, number][]; probItmLeftY: number; probItmRightY: number;
  eProbItmWeight: number; eProbItmEnabled: boolean;
  eProbItmPoints: [number, number][]; eProbItmLeftY: number; eProbItmRightY: number;
  pProbItmWeight: number; pProbItmEnabled: boolean;
  pProbItmPoints: [number, number][]; pProbItmLeftY: number; pProbItmRightY: number;
  /** Min of selected P/eP/pP curve scores. `minPitmSources` is `p,ep,pp`. */
  minPitmWeight: number; minPitmEnabled: boolean;
  minPitmPoints: [number, number][]; minPitmLeftY: number; minPitmRightY: number;
  minPitmSources: string;
  rorPoints: [number, number][]; rorLeftY: number; rorRightY: number;
  rorWeight: number;
  netCreditPoints: [number, number][]; netCreditLeftY: number; netCreditRightY: number;
  netCreditWeight: number;
  otmPoints: [number, number][]; otmLeftY: number; otmRightY: number;
  otmWeight: number; otmEnabled: boolean;
  absReturnPoints: [number, number][]; absReturnLeftY: number; absReturnRightY: number;
  absReturnWeight: number; absReturnEnabled: boolean;
  maxWidthPoints: [number, number][]; maxWidthLeftY: number; maxWidthRightY: number;
  maxWidthWeight: number; maxWidthEnabled: boolean;
}

/** Parse a config string to number, falling back to a default if NaN or empty. */
function num(val: string | undefined, def: number): number {
  if (val === undefined || val === '') return def;
  const n = Number(val);
  return isNaN(n) ? def : n;
}

/** DTE range derived from a DTE curve (`<prefix>_points` + `_left_y`/`_right_y`)
 *  — mirrors the Rust `screen::dte_fetch_range`. A reject edge (y < 0) clamps to
 *  the curve's first/last x; an open edge opens to the [1, 75] bound. This is the
 *  single source of truth for the DTE window. */
export function dteFetchRange(cfg: Record<string, string>, prefix: string): [number, number] {
  let pts: number[][] = [];
  try { pts = JSON.parse(cfg[`${prefix}_points`] || "[]"); } catch { pts = []; }
  const leftY = num(cfg[`${prefix}_left_y`], -1);
  const rightY = num(cfg[`${prefix}_right_y`], -1);
  const lo = pts.length ? pts[0][0] : 1;
  const hi = pts.length ? pts[pts.length - 1][0] : 75;
  const min = Math.max(1, leftY < 0 ? lo : 1);
  const max = Math.max(min, rightY < 0 ? hi : 75);
  return [min, max];
}

/** Per-parameter enable key. Hidden ⟺ `<key>="false"` in the per-account config —
 *  the single switch honored by the JS engine, the Rust engine, and the agent
 *  (they all load the same config). Mirrors the Rust registry's `<prefix>_enabled`. */
export const VS_PARAM_ENABLE_KEY: Record<string, string> = {
  fomc: "vs_avoid_fomc_enabled", fomcMinutes: "vs_avoid_fomc_minutes_enabled", fomcClose: "vs_close_before_fomc_enabled",
  // Rating/filter gates — disabling actually removes the gate from the engine.
  dte: "vs_dte_enabled", computeHv: "vs_compute_hv_enabled", computePItm: "vs_compute_pitm_enabled", junkQuote: "vs_junk_quote_enabled", minOi: "vs_min_oi_enabled", hvPct: "vs_hv_pct_enabled",
  baSpreadPct: "vs_ba_spread_pct_enabled", ivHv: "vs_iv_hv_enabled", probItm: "vs_prob_itm_enabled",
  eProbItm: "vs_e_prob_itm_enabled", pProbItm: "vs_p_prob_itm_enabled", minPitm: "vs_min_pitm_enabled", maxWidth: "vs_max_spread_width_enabled",
  netCredit: "vs_net_credit_enabled", ror: "vs_ror_enabled",
  otm: "vs_otm_enabled", absReturn: "vs_abs_return_enabled",
  rating: "vs_rating_enabled",
  // Sizing rows. The three contract-sizing methods are a mutually-exclusive
  // group (exactly one visible = active); the agent reads the same flags.
  totalExposure: "vs_total_exposure_enabled", callPutSplit: "vs_call_put_split_enabled",
  totalExposureDollars: "vs_total_exposure_dollars_enabled",
  // Per-direction trade size — each side (Put = bull, Call = bear) picks one of %/$/#.
  bullSizePct: "vs_bull_size_pct_enabled", bullSizeDollars: "vs_bull_size_dollars_enabled", bullSizeContracts: "vs_bull_size_contracts_enabled",
  bearSizePct: "vs_bear_size_pct_enabled", bearSizeDollars: "vs_bear_size_dollars_enabled", bearSizeContracts: "vs_bear_size_contracts_enabled",
  // Daily entry caps — independent; hidden = not enforced by the agent.
  maxTradesDay: "vs_max_trades_per_day_enabled",
  maxDollarsDay: "vs_max_dollars_per_day_enabled",
  maxPctDay: "vs_max_pct_account_per_day_enabled",
};

/** Enable keys that start hidden (false) when absent from account settings.
 *  Everything else in VS_PARAM_ENABLE_KEY defaults to visible (true). */
const VS_ENABLE_DEFAULT_FALSE = new Set([
  "vs_avoid_fomc_enabled",
  "vs_avoid_fomc_minutes_enabled",
  "vs_close_before_fomc_enabled",
  "vs_total_exposure_dollars_enabled",
  "vs_bull_size_dollars_enabled",
  "vs_bull_size_contracts_enabled",
  "vs_bear_size_dollars_enabled",
  "vs_bear_size_contracts_enabled",
  "vs_max_trades_per_day_enabled",
  "vs_max_dollars_per_day_enabled",
  "vs_max_pct_account_per_day_enabled",
]);

/** Fill missing `*_enabled` flags so hide/show state is always concrete in cfg
 *  (and therefore written on Save / Save as Defaults). Account rows win. */
export function applyEnableDefaults(cfg: Record<string, string>): Record<string, string> {
  const out = { ...cfg };
  for (const ek of Object.values(VS_PARAM_ENABLE_KEY)) {
    if (out[ek] === undefined) {
      out[ek] = VS_ENABLE_DEFAULT_FALSE.has(ek) ? "false" : "true";
    }
  }
  return out;
}

const on = (cfg: Record<string, string>, key: string) => cfg[key] !== "false";

/** Build the typed rating config from a string account-config map. */
export function buildRatingCfg(cfg: Record<string, string>): RatingCfg {
  return {
    baSpreadPctWeight: num(cfg.vs_ba_spread_pct_weight, 50), baSpreadPctEnabled: on(cfg, "vs_ba_spread_pct_enabled"),
    probItmWeight: num(cfg.vs_prob_itm_weight, 50), probItmEnabled: on(cfg, "vs_prob_itm_enabled"),
    hvPctWeight: num(cfg.vs_hv_pct_weight, 50), hvPctEnabled: on(cfg, "vs_hv_pct_enabled"),
    // IV/HV now gates whenever enabled (matches the Rust agent), not only when weight>0.
    ivHvWeight: num(cfg.vs_iv_hv_ratio_weight, 50), ivHvEnabled: on(cfg, "vs_iv_hv_enabled"),
    dteEnabled: on(cfg, "vs_dte_enabled"),
    dteWeight: num(cfg.vs_dte_weight, 0),
    oiEnabled: on(cfg, "vs_min_oi_enabled"),
    minOi: num(cfg.vs_min_oi, 10), minOiLeftY: num(cfg.vs_min_oi_left_y, -1), minOiRightY: num(cfg.vs_min_oi_right_y, 0),
    dtePoints: parsePoints(cfg.vs_dte_points, [[21, 100], [30, 100]]),
    dteLeftY: num(cfg.vs_dte_left_y, -1),
    dteRightY: num(cfg.vs_dte_right_y, -1),
    hvPctPoints: parsePoints(cfg.vs_hv_pct_points, [[0, 100], [100, 100]]),
    hvPctLeftY: num(cfg.vs_hv_pct_left_y, -1),
    hvPctRightY: num(cfg.vs_hv_pct_right_y, 0),
    baSpreadPctPoints: parsePoints(cfg.vs_ba_spread_pct_points, [[5, 0], [10, 100], [20, 0]]),
    baSpreadPctLeftY: num(cfg.vs_ba_spread_pct_left_y, 0),
    baSpreadPctRightY: num(cfg.vs_ba_spread_pct_right_y, -1),
    ivHvPoints: parsePoints(cfg.vs_iv_hv_points, [[0.8, 0], [1.2, 100], [1.8, 0]]),
    ivHvLeftY: num(cfg.vs_iv_hv_left_y, 0),
    ivHvRightY: num(cfg.vs_iv_hv_right_y, 0),
    probItmPoints: parsePoints(cfg.vs_prob_itm_points, [[3, 0], [12, 100], [35, 0]]),
    probItmLeftY: num(cfg.vs_prob_itm_left_y, -1),
    probItmRightY: num(cfg.vs_prob_itm_right_y, -1),
    eProbItmWeight: num(cfg.vs_e_prob_itm_weight, 0), eProbItmEnabled: num(cfg.vs_e_prob_itm_weight, 0) > 0,
    eProbItmPoints: parsePoints(cfg.vs_e_prob_itm_points, [[3, 0], [12, 100], [35, 0]]),
    eProbItmLeftY: num(cfg.vs_e_prob_itm_left_y, -1),
    eProbItmRightY: num(cfg.vs_e_prob_itm_right_y, -1),
    pProbItmWeight: num(cfg.vs_p_prob_itm_weight, 0), pProbItmEnabled: num(cfg.vs_p_prob_itm_weight, 0) > 0,
    pProbItmPoints: parsePoints(cfg.vs_p_prob_itm_points, [[3, 0], [12, 100], [35, 0]]),
    pProbItmLeftY: num(cfg.vs_p_prob_itm_left_y, -1),
    pProbItmRightY: num(cfg.vs_p_prob_itm_right_y, -1),
    minPitmWeight: num(cfg.vs_min_pitm_weight, 0), minPitmEnabled: on(cfg, "vs_min_pitm_enabled"),
    minPitmPoints: parsePoints(cfg.vs_min_pitm_points, [[3, 0], [12, 100], [35, 0]]),
    minPitmLeftY: num(cfg.vs_min_pitm_left_y, 0),
    minPitmRightY: num(cfg.vs_min_pitm_right_y, 0),
    minPitmSources: cfg.vs_min_pitm_sources || "p,ep,pp",
    rorPoints: parsePoints(cfg.vs_ror_points, [[50, 0], [200, 100]]),
    rorLeftY: num(cfg.vs_ror_left_y, -1),
    rorRightY: num(cfg.vs_ror_right_y, 0),
    rorWeight: num(cfg.vs_ror_weight, 20),
    netCreditPoints: parsePoints(cfg.vs_net_credit_points, [[0.6, 100], [2, 100]]),
    netCreditLeftY: num(cfg.vs_net_credit_left_y, -1),
    netCreditRightY: num(cfg.vs_net_credit_right_y, 0),
    netCreditWeight: num(cfg.vs_net_credit_weight, 0),
    otmPoints: parsePoints(cfg.vs_otm_points, [[0, 0], [10, 100]]),
    otmLeftY: num(cfg.vs_otm_left_y, 0),
    otmRightY: num(cfg.vs_otm_right_y, 0),
    otmWeight: num(cfg.vs_otm_weight, 0), otmEnabled: on(cfg, "vs_otm_enabled"),
    absReturnPoints: parsePoints(cfg.vs_abs_return_points, [[0, 0], [20, 100]]),
    absReturnLeftY: num(cfg.vs_abs_return_left_y, 0),
    absReturnRightY: num(cfg.vs_abs_return_right_y, 0),
    absReturnWeight: num(cfg.vs_abs_return_weight, 0), absReturnEnabled: on(cfg, "vs_abs_return_enabled"),
    maxWidthPoints: parsePoints(cfg.vs_max_spread_width_points, [[1, 100], [8, 100], [10, 50], [15, 50]]),
    maxWidthLeftY: num(cfg.vs_max_spread_width_left_y, 0),
    maxWidthRightY: num(cfg.vs_max_spread_width_right_y, -1),
    maxWidthWeight: num(cfg.vs_max_spread_width_weight, 0), maxWidthEnabled: on(cfg, "vs_max_spread_width_enabled"),
  };
}
